import Payment from "src/app/pojos/payment";
import { ActivatedRoute } from "@angular/router";
import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgxSpinnerService } from "ngx-spinner";
import { RzpPaymentService } from "src/app/services/rzp-payment.service";
import { TosterService } from "src/app/services/toster.service";
import { UtilService } from "src/app/services/util.service";
import { PaymentApiService } from "src/app/api/payment-api.service";
import { CountryList, stateList } from "../plan/invoice-dialog/states";
import { BillingInfo } from "src/app/services/payment-state.service";
import * as moment from "moment";

@Component({
  selector: "app-biometric-buy",
  templateUrl: "./biometric-buy.component.html",
  styleUrls: ["./biometric-buy.component.scss"],
  standalone: false,
})
export class BiometricBuyComponent implements OnInit {
  isGstFeatureEnabled: boolean = true;
  perMemberPerDay: number = 0.0583;
  memberCount: number = 200;
  memberOptions: number[] = [
    50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000,
  ];
  subscriptionExpiryDate: Date = null;
  daysRemaining: number = 0;
  redirectUrl: string = null;
  subscriberDetail: any = {};
  selectedBranchId: string = null;
  isLoading: boolean = false;
  showValidationError: boolean = false;

  // Billing form (India only)
  billingForm!: FormGroup;
  countries = CountryList;
  states = stateList;
  districts: string[] = [];

  // Filtered options for autocomplete
  filteredStates = this.states;
  filteredDistricts: string[] = [];
  filteredCountries = this.countries;

  constructor(
    private spinner: NgxSpinnerService,
    private rzpService: RzpPaymentService,
    private toast: TosterService,
    private util: UtilService,
    private route: ActivatedRoute,
    private paymentApi: PaymentApiService,
    private fb: FormBuilder
  ) {}

  async ngOnInit() {
    this.redirectUrl = this.route.snapshot.queryParamMap.get("redirectUrl");
    let subId = this.route.snapshot.queryParamMap.get("subID");
    if (subId) {
      this.selectedBranchId = subId;
    }

    // Set India as default and first in the list
    this.setIndiaAsDefault();
    this.initializeBillingForm();
    this.loadSavedBillingAddress();

    // Get subscriber details to fetch subscription expiry date
    await this.getSubscriberDetails();
  }

  private setIndiaAsDefault() {
    // Find India in the countries list
    const indiaIndex = this.countries.findIndex((c) => c.value === "IN");
    if (indiaIndex > 0) {
      // Move India to the first position
      const india = this.countries.splice(indiaIndex, 1)[0];
      this.countries.unshift(india);
    }
    // Update filtered countries to match the new order
    this.filteredCountries = [...this.countries];
  }

  private initializeBillingForm() {
    this.billingForm = this.fb.group({
      country: [
        this.countries.find((c) => c.value === "IN"),
        Validators.required,
      ],
      state: ["", Validators.required],
      district: ["", Validators.required],
      pinCode: [
        "",
        [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
      ],
      addressLine: ["", Validators.required],
      gymName: [""],
      hasGST: [false, Validators.required],
      gstNumber: [""],
      memberCount: [this.memberCount, Validators.required],
    });

    // GST conditional validator
    this.billingForm.get("hasGST")?.valueChanges.subscribe((hasGST) => {
      const gstCtrl = this.billingForm.get("gstNumber");
      if (hasGST) {
        gstCtrl?.setValidators([
          Validators.required,
          Validators.pattern(
            /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
          ),
        ]);
      } else {
        gstCtrl?.clearValidators();
      }
      gstCtrl?.updateValueAndValidity();
    });

    // Calculate price when member count changes
    this.billingForm.get("memberCount")?.valueChanges.subscribe((count) => {
      if (count) {
        this.memberCount = count;
        this.calculateDaysRemaining();
      }
    });
  }

  private calculateDaysRemaining() {
    if (!this.subscriptionExpiryDate) {
      this.daysRemaining = 0;
      return;
    }

    const today = moment().startOf("day");
    const expiry = moment(this.subscriptionExpiryDate).startOf("day");
    this.daysRemaining = expiry.diff(today, "days");

    if (this.daysRemaining <= 0) {
      this.daysRemaining = 0;
    }
  }

  private async loadSavedBillingAddress() {
    try {
      const res = await this.paymentApi.getBillingAddress(
        this.selectedBranchId
      );
      const data = res?.data;
      if (!data) return;

      // Map country label -> object
      const countryObj =
        this.countries.find((c) => c.label === (data.country || "India")) ||
        this.countries.find((c) => c.value === "IN");

      // Map state name -> object
      const stateObj = this.states.find((s) => s.state === data.state) || null;
      const districts = stateObj?.districts || [];
      this.districts = districts;

      this.billingForm.patchValue({
        country: countryObj,
        state: stateObj,
        district: data.district || "",
        pinCode: data.pinCode || "",
        addressLine: data.addressLine || "",
        gymName: data.gymName || "",
        hasGST: !!data.hasGST,
        gstNumber: data.gstNumber || "",
      });

      // Update districts and filtered districts after patching state
      if (stateObj) {
        this.districts = stateObj.districts || [];
        this.filteredDistricts = this.districts;
      }
    } catch (error) {
      // Silently handle prefilling errors - form will start empty
      console.warn("Failed to load saved billing address:", error);
    }
  }

  getBaseAmount() {
    if (this.daysRemaining <= 0 || !this.memberCount) {
      return 0;
    }
    const totalPrice =
      this.daysRemaining * this.perMemberPerDay * this.memberCount;
    return Math.ceil(totalPrice);
  }

  getGstAmount() {
    const baseAmount = this.getBaseAmount();
    return Math.ceil(baseAmount * 0.18);
  }

  getTotalAmountWithGst() {
    const baseAmount = this.getBaseAmount();
    const gstAmount = this.getGstAmount();
    return Math.ceil(baseAmount + gstAmount).toString();
  }

  private prepareBillingInfo(formData: Record<string, any>): BillingInfo {
    const billingInfo: BillingInfo = {
      country: this.getCountryLabel(formData.country),
      addressLine: formData.addressLine,
      gymName: formData.gymName || "",
      hasGST: formData.hasGST || false,
      gstNumber: formData.gstNumber || "",
      state: this.getStateName(formData.state),
      district: formData.district || "",
      pinCode: formData.pinCode || "",
    };

    return billingInfo;
  }

  private getCountryLabel(country: any): string {
    if (typeof country === "object" && country?.label) {
      return country.label;
    } else if (typeof country === "string") {
      const selectedCountry = this.countries.find((c) => c.value === country);
      return selectedCountry?.label || country;
    }
    return "";
  }

  private getStateName(state: any): string {
    if (typeof state === "object" && state?.state) {
      return state.state;
    }
    return state || "";
  }

  async payNow() {
    // Validate expiry date
    if (!this.subscriptionExpiryDate || this.daysRemaining <= 0) {
      this.toast.showErrorToast(
        "Subscription expiry date is missing or invalid. Please ensure your subscription is active."
      );
      return;
    }

    // Validate billing form
    if (this.billingForm.invalid) {
      this.billingForm.markAllAsTouched();
      this.showValidationError = true;

      // Hide error message after 8 seconds
      setTimeout(() => {
        this.showValidationError = false;
      }, 8000);

      // Scroll to first error
      setTimeout(() => {
        const firstError = document.querySelector(".mat-form-field.ng-invalid");
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
      return;
    }

    const billingInfo = this.prepareBillingInfo(this.billingForm.value);

    try {
      const payment: Payment =
        await this.rzpService.createPaymentRequestForBiometric(
          this.memberCount,
          this.perMemberPerDay,
          this.selectedBranchId,
          billingInfo
        );

      const orderId = await this.rzpService.createRzpOrder(
        payment.finalAmount,
        payment.notes.paymentId,
        payment.currency
      );

      await this.rzpService.acceptPaymentFromRzp(payment, orderId);

      if (this.util.isNotNull(this.redirectUrl)) {
        window.open(this.redirectUrl, "_self");
      } else {
        window.open(location.origin + "/payment?s=s", "_self");
      }
    } catch (error) {
      this.spinner.hide();
      if (this.util.isNotNull(error?.error?.message)) {
        this.toast.showErrorToast(error.error.message);
      } else {
        window.open(location.origin + "/payment?s=f", "_self");
      }
    }
  }

  async getSubscriberDetails() {
    this.isLoading = true;
    try {
      let subscriber = await this.paymentApi.getSubscriberDetail();
      this.subscriberDetail = {
        subName: subscriber.data.subName,
        email: subscriber.data.email,
        name: subscriber.data.name,
      };
    } catch (error) {
      console.error("Failed to load subscriber details:", error);
    } finally {
      this.isLoading = false;
    }
  }

  onBranchSelected(branchId: string): void {
    this.selectedBranchId = branchId;
  }

  onSubscriptionExpiryDate(subscriptionExpiryDate: Date): void {
    this.subscriptionExpiryDate = subscriptionExpiryDate;
    this.calculateDaysRemaining();
  }

  /**
   * Filter states based on user input
   * @param event - Input event from the state autocomplete field
   */
  onStateInput(event: any): void {
    const value = event.target.value.toLowerCase();
    this.filteredStates = this.states.filter((state) =>
      state.state.toLowerCase().includes(value)
    );

    // Clear district when state input changes
    this.billingForm.get("district")?.setValue("");
    this.districts = [];
    this.filteredDistricts = [];
  }

  /**
   * Handle state selection from autocomplete dropdown
   * @param state - Selected state object containing districts array
   */
  onStateSelected(state: any): void {
    if (state) {
      // Update the form control with the selected state
      this.billingForm.get("state")?.setValue(state);

      // Update districts and auto-select first district
      this.districts = state.districts || [];
      this.filteredDistricts = [...this.districts];

      // Auto-select first district
      if (this.districts.length > 0) {
        const firstDistrict = this.districts[0];
        this.billingForm.get("district")?.setValue(firstDistrict);
      }

      // Force change detection
      setTimeout(() => {
        this.filteredDistricts = [...this.districts];
      }, 0);
    }
  }

  getStateDisplayValue(state: any): string {
    return state ? state.state : "";
  }

  // TrackBy function for districts
  trackByDistrict(index: number, district: string): string {
    return district;
  }

  // District filtering
  onDistrictInput(event: any): void {
    const value = event.target.value.toLowerCase();
    this.filteredDistricts = this.districts.filter((district) =>
      district.toLowerCase().includes(value)
    );
  }

  // Handle district selection from autocomplete
  onDistrictSelected(district: any): void {
    if (district) {
      this.billingForm.get("district")?.setValue(district);
    }
  }

  /**
   * Filter countries based on user input
   * @param event - Input event from the country autocomplete field
   */
  onCountryInput(event: any): void {
    const value = event.target.value.toLowerCase();
    this.filteredCountries = this.countries.filter((country) =>
      country.label.toLowerCase().includes(value)
    );
  }

  /**
   * Handle country selection from autocomplete dropdown
   * @param country - Selected country object
   */
  onCountrySelected(country: any): void {
    if (country) {
      this.billingForm.get("country")?.setValue(country);
    }
  }

  /**
   * Display function for country autocomplete
   * @param country - Country object to display
   */
  getCountryDisplayValue(country: any): string {
    return country ? country.label : "";
  }
}
